/*
  Motureo (W1): strona produktu — „Sprawdź numer OE” (blocks/motureo-oe-check.liquid) i „Ostatnio oglądane”
  (sections/motureo-recent.liquid). Bez zależności. Numer OE porównujemy tylko z listą z danych produktu
  (metapole motureo.oe_numbers); wynik nie jest obietnicą dopasowania do auta. Czyste funkcje (porównanie numeru,
  lista „ostatnio oglądane”) są w assets/motureo-logika.js, ładowanym wcześniej jako zwykły skrypt.
*/

const logic = () => /** @type {any} */ (globalThis).MotureoLogika;

/**
 * Podpowiedź w polu OE: długa, gdy mieści się w polu, inaczej krótka z data-placeholder-short, a gdy i ta się nie
 * mieści (320 px) — brak podpowiedzi, bo nad polem jest etykieta. Na telefonie pole ma 106–176 px i długi tekst był
 * ucięty (zgłoszenie właściciela 19.10). Szerokość tekstu mierzona czcionką pola.
 * @param {HTMLInputElement} input
 */
function fitPlaceholder(input) {
  const long = input.placeholder;
  const short = input.dataset.placeholderShort;
  if (!short || !long || short === long || typeof ResizeObserver === 'undefined') return;
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return;
  const update = () => {
    const cs = getComputedStyle(input);
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const room = input.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    const fits = (/** @type {string} */ text) => room <= 0 || ctx.measureText(text).width <= room;
    const next = fits(long) ? long : fits(short) ? short : '';
    if (input.placeholder !== next) input.placeholder = next;
  };
  new ResizeObserver(update).observe(input);
  document.fonts?.ready.then(update); // własna czcionka zmienia szerokość tekstu po załadowaniu
}

class MotureoOeCheck extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('[data-oe-form]');
    const input = this.querySelector('[data-oe-input]');
    const result = this.querySelector('[data-oe-result]');
    // L1b: pomoc po wyniku „nie znaleziono” — kontakt i wyszukiwanie tego numeru w katalogu (bez ślepego zaułka)
    const help = this.querySelector('[data-oe-help]');
    const searchLink = this.querySelector('[data-oe-search]');
    if (!form || !input || !result) return;
    const searchBase = searchLink ? searchLink.getAttribute('href') || '' : '';
    fitPlaceholder(input);
    let numbers = [];
    try {
      numbers = JSON.parse(this.dataset.numbers || '[]');
    } catch {
      numbers = [];
    }
    const showHelp = (/** @type {string} */ value) => {
      if (!help) return;
      help.hidden = !value;
      if (searchLink && value) {
        searchLink.setAttribute('href', `${searchBase}${searchBase.includes('?') ? '&' : '?'}q=${encodeURIComponent(value)}`);
      }
    };
    form.addEventListener('submit', (event) => {
      const L = logic();
      if (!L) return; // bez skryptu z funkcjami formularz działa jak wyszukiwanie
      event.preventDefault();
      result.classList.remove('is-match', 'is-miss');
      if (!L.normalizeOe(input.value)) {
        result.textContent = this.dataset.empty || '';
        showHelp('');
        input.focus();
        return;
      }
      const found = L.findOe(input.value, numbers);
      if (found) {
        result.textContent = (this.dataset.match || '').replace('[numer]', found);
        result.classList.add('is-match');
        showHelp('');
      } else {
        result.textContent = this.dataset.noMatch || '';
        result.classList.add('is-miss');
        showHelp(input.value.trim());
      }
    });
    input.addEventListener('input', () => {
      if (!input.value) {
        result.textContent = '';
        result.classList.remove('is-match', 'is-miss');
        showHelp('');
      }
    });
  }
}

if (!customElements.get('motureo-oe-check')) customElements.define('motureo-oe-check', MotureoOeCheck);

/* Okno powiększenia zdjęć Horizona (zoom-dialog): strzałki lewo/prawo przełączają zdjęcia (Esc zamyka już Horizon). */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
  const dialog = document.querySelector('zoom-dialog dialog[open]');
  if (!dialog) return;
  const zoom = /** @type {any} */ (dialog.closest('zoom-dialog'));
  const buttons = [...dialog.querySelectorAll('button[aria-selected]')];
  if (!zoom || typeof zoom.selectThumbnail !== 'function' || buttons.length < 2) return;
  const current = Math.max(0, buttons.findIndex((b) => b.getAttribute('aria-selected') === 'true'));
  const step = event.key === 'ArrowRight' ? 1 : -1;
  event.preventDefault();
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  zoom.selectThumbnail((current + step + buttons.length) % buttons.length, { behavior });
});

const STORAGE_KEY = 'motureo:recent';

function readRecent() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((p) => p && typeof p.handle === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* pamięć przeglądarki niedostępna (tryb prywatny): lista nie powstaje */
  }
}

const safeUrl = (url) => (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') ? url : null);
const safeImage = (url) =>
  typeof url === 'string' && (url.startsWith('//') || url.startsWith('https://')) ? url : null;

class MotureoRecent extends HTMLElement {
  connectedCallback() {
    const L = logic();
    if (!L) return;
    const current = this.dataset.current || '';
    this.#render(L.visibleRecent(readRecent(), current, this.dataset.locale || ''));

    const data = this.querySelector('[data-recent-product]');
    if (data) {
      try {
        const product = JSON.parse(data.textContent || '{}');
        if (product.handle && safeUrl(product.url)) {
          writeRecent(L.addRecent(readRecent(), product));
        }
      } catch {
        /* niepoprawne dane karty: pomijamy zapis */
      }
    }

    this.querySelector('[data-recent-clear]')?.addEventListener('click', () => {
      writeRecent(current ? readRecent().filter((p) => p.handle === current) : []);
      this.hidden = true;
    });
  }

  #render(products) {
    const list = this.querySelector('[data-recent-list]');
    if (!list) return;
    list.replaceChildren();
    for (const product of products) {
      const url = safeUrl(product.url);
      if (!url) continue;
      const item = document.createElement('li');
      item.className = 'motureo-recent__item';
      const link = document.createElement('a');
      link.className = 'motureo-recent__card';
      link.href = url;
      const media = document.createElement('span');
      media.className = 'motureo-recent__media';
      const image = safeImage(product.image);
      if (image) {
        const img = document.createElement('img');
        img.src = image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 400;
        img.height = 400;
        media.append(img);
      }
      const title = document.createElement('span');
      title.className = 'motureo-recent__title';
      title.textContent = String(product.title || '');
      link.append(media, title);
      if (product.price && product.currency === this.dataset.currency) {
        const price = document.createElement('span');
        price.className = 'motureo-recent__price';
        price.textContent = String(product.price);
        link.append(price);
      }
      item.append(link);
      list.append(item);
    }
    this.hidden = list.children.length === 0;
  }
}

if (!customElements.get('motureo-recent')) customElements.define('motureo-recent', MotureoRecent);
